import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Alert,
  Pressable,
  Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { AntDesign } from '@expo/vector-icons';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authSheetVisibleAtom } from '../atoms';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';

export default function AuthBottomSheet() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { signInWithGoogle, signInWithApple } = useAuth();
  const [isVisible, setIsVisible] = useAtom(authSheetVisibleAtom);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      setIsVisible(false);
    } catch (error) {
      Alert.alert(t('common.error'), t('errors.signInFailed'));
    }
  };

  const handleAppleSignIn = async () => {
    try {
      await signInWithApple();
      setIsVisible(false);
    } catch (error) {
      Alert.alert(t('common.error'), t('errors.signInFailed'));
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={() => setIsVisible(false)}
      onSwipeComplete={() => setIsVisible(false)}
      swipeDirection={['down']}
      style={styles.modal}
      backdropOpacity={0.5}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      customBackdrop={
        <Pressable 
          style={[
            styles.backdrop,
            Platform.OS === 'web' && {
              position: 'fixed' as any,
              width: '100vw' as any,
              height: '100vh' as any,
            }
          ]} 
          onPress={() => setIsVisible(false)}
        />
      }
    >
      <View style={[styles.content, { backgroundColor: colors.background }]}>
        <View style={[styles.indicator, { backgroundColor: colors.border }]} />
        <Text style={[styles.title, fontStyle, { color: colors.text }]}>{t('auth.signInRequired')}</Text>
        <Text style={[styles.description, fontStyle, { color: colors.textSecondary }]}>{t('auth.signInDescription')}</Text>

        <View style={styles.buttonsContainer}>
          {/* Google Sign In Button */}
          <Pressable onPress={handleGoogleSignIn}>
            <PixelContainer backgroundColor="#4285F4" cornerSize={4} style={styles.button}>
              <View style={styles.buttonContent}>
                <AntDesign name="google" size={20} color="#fff" />
                <Text style={[styles.buttonText, fontStyle]}>{t('auth.continueWithGoogle')}</Text>
              </View>
            </PixelContainer>
          </Pressable>

          {/* Apple Sign In Button - disabled on web until properly configured */}
          {/* Requires Apple Developer Service ID configuration for web */}
          {/* <Pressable onPress={handleAppleSignIn}>
            <PixelContainer backgroundColor="#000000" cornerSize={4} style={styles.button}>
              <View style={styles.buttonContent}>
                <AntDesign name="apple1" size={20} color="#fff" />
                <Text style={[styles.buttonText, fontStyle]}>{t('auth.continueWithApple')}</Text>
              </View>
            </PixelContainer>
          </Pressable> */}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    padding: 24,
    paddingTop: 12,
    alignItems: 'center',
  },
  indicator: {
    width: 40,
    height: 4,
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 32,
  },
  buttonsContainer: {
    width: '100%',
    gap: 12,
  },
  button: {
    padding: 14,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
